
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Poll } from '@/types/poll';
import { usePollRealtime } from './usePollRealtime';
import { transformPollsFromDb } from '@/utils/polls/pollDataTransformers';

interface PollFetchProps {
  includeAllStatuses?: boolean;
  countryCode?: string | null;
}

export const usePollFetch = ({ 
  includeAllStatuses = false,
  countryCode = null 
}: PollFetchProps = {}) => {
  const [polls, setPolls] = useState<Poll[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Set up real-time updates
  usePollRealtime(setPolls, countryCode || undefined);

  const fetchPolls = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('Fetching polls:', { includeAllStatuses, countryCode });
      
      // Build query
      let query = supabase.from('polls').select('*');
      
      // If not including all statuses, only fetch live polls
      if (!includeAllStatuses) {
        query = query.eq('status', 'live');
      }

      // Add country filter if provided
      if (countryCode) {
        query = query.eq('country_code', countryCode);
      }
      
      // Always sort by created date, newest first
      query = query.order('created_at', { ascending: false });
      
      // Execute query
      const { data, error: fetchError } = await query;
      
      if (fetchError) {
        throw fetchError;
      }
      
      if (data) {
        // Transform the data
        const transformedPolls = transformPollsFromDb(data);
        
        // Ensure all polls have a total_votes property
        const pollsWithTotalVotes = transformedPolls.map(poll => {
          if (poll.total_votes === undefined) {
            const totalVotes = poll.options.reduce((sum, option) => sum + (option.votes || 0), 0);
            return { ...poll, total_votes: totalVotes };
          }
          return poll;
        });
        
        console.log(`Fetched ${pollsWithTotalVotes.length} polls`);
        setPolls(pollsWithTotalVotes);
      }
      
      setError(null);
    } catch (fetchError) {
      console.error('Error fetching polls:', fetchError);
      setError(fetchError as Error);
    } finally {
      setIsLoading(false);
    }
  }, [includeAllStatuses, countryCode]);

  // Initial fetch
  useEffect(() => {
    fetchPolls();
  }, [fetchPolls]);

  // Simple refetch function for external triggers
  const refetchPolls = useCallback(() => {
    console.log('Poll refetch requested');
    return fetchPolls();
  }, [fetchPolls]);

  return {
    polls,
    setPolls,
    isLoading,
    error,
    refetchPolls
  };
};
